import { useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { Offerable, OfferablePokemon } from '../lib/types';
import { Button, EmptyState, Modal } from './ui';
import { formatNumber } from '../lib/format';

/**
 * The "give something away" form, shared by Community Projects and guilds.
 *
 * The server has one giving path for both, so the client has one form for both
 * — otherwise the two drift, and the one that drifts is the one that forgets to
 * warn you that a gift is permanent.
 *
 * ## Selection has to be unmissable
 *
 * A gift is a **sink**: what goes in stops existing. Players reported not being
 * able to tell which Pokémon they had selected, which on this particular screen
 * is not a cosmetic complaint — it is the difference between donating a spare
 * Rattata and donating a Shiny. The selected state was a class with no
 * stylesheet rule behind it at all, so a chosen card and an unchosen one looked
 * identical.
 *
 * So a selected card changes on four axes at once — background, border, a
 * corner tick, and a "SELECTED" label — and never on colour alone, which is
 * both an accessibility requirement and the only way it survives being scanned
 * at speed across forty cards. Hover is deliberately a *different* and much
 * quieter change, so "the one under my cursor" can never be mistaken for "the
 * ones I picked".
 */
export interface Gift {
  coins?: number;
  items?: Array<{ itemKey: string; quantity: number }>;
  pokemonIds?: string[];
}

const MAX_POKEMON = 12;

type Filter = 'all' | 'low' | 'duplicate';

export function GiveForm({
  onGive,
  onCancel,
  coinStep = 250,
}: {
  onGive: (gift: Gift) => Promise<void>;
  onCancel: () => void;
  coinStep?: number;
}) {
  const { push } = useToast();
  // One endpoint: what is spare does not depend on where it is going.
  const offerable = useAsync<Offerable>(() => api.get('/me/offerable'));
  const [coins, setCoins] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  const [items, setItems] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [confirming, setConfirming] = useState(false);

  const pokemon = offerable.data?.pokemon ?? [];
  const chosenItems = Object.entries(items).filter(([, quantity]) => quantity > 0);
  const nothingChosen = coins === 0 && picked.length === 0 && chosenItems.length === 0;

  /**
   * Selection is held as ids, not as a filtered slice, which is what keeps it
   * stable when the list underneath moves. Searching or filtering hides cards;
   * it must never quietly un-pick them, because "I filtered and it forgot half
   * my selection" is how somebody donates the wrong six.
   */
  const pickedSet = new Set(picked);

  const speciesCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const mon of pokemon) counts.set(mon.species.id, (counts.get(mon.species.id) ?? 0) + 1);
    return counts;
  }, [pokemon]);

  const visible = pokemon.filter((mon) => {
    const term = search.trim().toLowerCase();
    if (term && !mon.displayName.toLowerCase().includes(term) && !mon.species.displayName.toLowerCase().includes(term)) {
      return false;
    }
    if (filter === 'low') return mon.level <= 10;
    if (filter === 'duplicate') return (speciesCounts.get(mon.species.id) ?? 0) > 1;
    return true;
  });

  const pickedRows = pokemon.filter((mon) => pickedSet.has(mon.id));
  const visiblePicked = visible.filter((mon) => pickedSet.has(mon.id)).length;
  const notableRows = pickedRows.filter((mon) => (mon.notable?.length ?? 0) > 0);

  function toggle(mon: OfferablePokemon) {
    setPicked((current) => {
      if (current.includes(mon.id)) return current.filter((id) => id !== mon.id);
      if (current.length >= MAX_POKEMON) {
        push('info', `You can give at most ${MAX_POKEMON} Pokémon at once.`);
        return current;
      }
      return [...current, mon.id];
    });
  }

  async function submit() {
    setBusy(true);
    try {
      await onGive({
        coins: coins || undefined,
        pokemonIds: picked.length > 0 ? picked : undefined,
        items: chosenItems.map(([itemKey, quantity]) => ({ itemKey, quantity })),
      });
      setConfirming(false);
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not give that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
        <div className="row row--between">
          <span className="field__label">PokeCoins</span>
          <span className="tiny muted mono">
            you have {formatNumber(offerable.data?.pokeCoins ?? 0)}
          </span>
        </div>
        <div className="row">
          <input
            className="input"
            type="number"
            min={0}
            step={coinStep}
            max={offerable.data?.pokeCoins ?? 0}
            value={coins || ''}
            placeholder="0"
            onChange={(event) => setCoins(Math.max(0, Number(event.target.value)))}
          />
          <Button size="sm" onClick={() => setCoins(Math.min(offerable.data?.pokeCoins ?? 0, 5000))}>
            5k
          </Button>
          <Button size="sm" onClick={() => setCoins(0)}>
            Clear
          </Button>
        </div>
      </div>

      {(offerable.data?.items.length ?? 0) > 0 && (
        <div className="stack-sm">
          <div className="field__label">Supplies</div>
          <div className="row row--wrap">
            {offerable.data!.items.map((item) => (
              <button
                key={item.itemKey}
                type="button"
                className={`pxwell give-card ${items[item.itemKey] ? 'give-card--picked' : ''}`}
                style={{ width: 120 }}
                aria-pressed={Boolean(items[item.itemKey])}
                onClick={() =>
                  setItems((current) => {
                    const next = { ...current };
                    const value = (next[item.itemKey] ?? 0) + 1;
                    if (value > item.quantity) delete next[item.itemKey];
                    else next[item.itemKey] = value;
                    return next;
                  })
                }
              >
                <div className="tiny mono">{item.name}</div>
                <div className="tiny muted">
                  {items[item.itemKey] ?? 0} / {item.quantity}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="stack-sm">
        <div className="row row--between">
          <span className="field__label">Pokémon</span>
          <span className="tiny muted">
            Locked, favourite, Team, Grove and Nursery Pokémon are never offered here.
          </span>
        </div>

        {pokemon.length > 6 && (
          <div className="row row--wrap" style={{ gap: 4 }}>
            <input
              className="input"
              style={{ flex: 1, minWidth: 140 }}
              value={search}
              placeholder="Search"
              onChange={(event) => setSearch(event.target.value)}
            />
            {(['all', 'low', 'duplicate'] as Filter[]).map((key) => (
              <button
                key={key}
                type="button"
                className={`chip ${filter === key ? 'chip--on' : ''}`}
                onClick={() => setFilter(key)}
              >
                {key === 'all' ? 'All' : key === 'low' ? 'Lv 10 and under' : 'Duplicates'}
              </button>
            ))}
          </div>
        )}

        {offerable.loading ? (
          <div className="pxwell skeleton" style={{ height: 120 }} />
        ) : pokemon.length === 0 ? (
          <EmptyState art="◌" title="Nothing spare">
            Everything you own is locked, favourited, on your Team, or out in your Grove.
          </EmptyState>
        ) : (
          <>
            <div className="give-grid">
              {visible.map((mon) => {
                const isPicked = pickedSet.has(mon.id);
                return (
                  <button
                    key={mon.id}
                    type="button"
                    className={`pxframe give-card ${isPicked ? 'give-card--picked' : ''}`}
                    // The state is on the element as well as in the styling, so
                    // a screen reader gets it without seeing the tint at all.
                    aria-pressed={isPicked}
                    onClick={() => toggle(mon)}
                  >
                    <span className="give-card__tick" aria-hidden>
                      {isPicked ? '✓' : ''}
                    </span>
                    <img className="pixel" src={mon.sprite} alt="" loading="lazy" />
                    <div className="tiny mono">{mon.displayName}</div>
                    <div className="tiny muted">Lv {mon.level}</div>

                    {/* The reasons come from the server — the client cannot see
                        markings, generation or bloodline founding. */}
                    {(mon.notable?.length ?? 0) > 0 && (
                      <div className="give-card__notable tiny" title={mon.notable!.map((r) => r.label).join(' · ')}>
                        ⚠ {mon.notable![0]!.label}
                      </div>
                    )}

                    <span className="give-card__state tiny">{isPicked ? 'SELECTED' : ''}</span>
                  </button>
                );
              })}
            </div>

            {visible.length === 0 && (
              <p className="tiny muted" style={{ margin: 0 }}>
                Nothing matches that. Your selection is still here.
              </p>
            )}

            {/* The counter never lies about what is hidden. Filtering does not
                deselect, so a player who narrows the list must still be able to
                see that four of their six are off screen. */}
            <div className="row row--between tiny">
              <strong>
                {picked.length} / {MAX_POKEMON} selected
              </strong>
              <span className="muted">
                {picked.length > visiblePicked
                  ? `${visiblePicked} shown, ${picked.length - visiblePicked} hidden by the filter`
                  : `${visible.length} shown`}
              </span>
            </div>

            {picked.length > 0 && (
              <button type="button" className="chip" onClick={() => setPicked([])}>
                Clear selection
              </button>
            )}
          </>
        )}
      </div>

      {/* No "select all". Everything on this screen is destroyed by the button
          at the bottom, and a control that fills the basket in one click is the
          one that turns a misread into a catastrophe. Clearing is one click;
          choosing is deliberate. */}

      <div className="row row--between row--wrap">
        <span className="tiny muted">
          {picked.length > 0 && `${picked.length} Pokémon · `}
          {coins > 0 && `${formatNumber(coins)} coins · `}
          {nothingChosen ? 'Nothing chosen yet' : 'Given for good'}
        </span>
        <div className="row" style={{ gap: 6 }}>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="go" disabled={nothingChosen || busy} onClick={() => setConfirming(true)}>
            Give it
          </Button>
        </div>
      </div>

      {confirming && (
        <ConfirmGift
          coins={coins}
          items={chosenItems}
          pokemon={pickedRows}
          notable={notableRows}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void submit()}
        />
      )}
    </div>
  );
}

/**
 * The review step.
 *
 * A separate confirm rather than a "are you sure?" on the button, because the
 * thing worth reviewing is *what is in the gift* — a player who miscounted or
 * mis-tapped finds out here, from a list, rather than from a toast afterwards.
 * There is no undo on the other side of this button.
 */
function ConfirmGift({
  coins,
  items,
  pokemon,
  notable,
  busy,
  onCancel,
  onConfirm,
}: {
  coins: number;
  items: Array<[string, number]>;
  pokemon: OfferablePokemon[];
  notable: OfferablePokemon[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // A second, typed confirmation only when something genuinely rare is in the
  // basket. Asking twice for a spare Rattata trains people to click through the
  // dialog that matters.
  const [acknowledged, setAcknowledged] = useState(false);
  const needsAck = notable.length > 0;

  return (
    <Modal onClose={onCancel} labelledBy="give-confirm-title">
      <div className="stack">
        <h2 id="give-confirm-title">Give these away?</h2>

        <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
          {coins > 0 && <div className="tiny">{formatNumber(coins)} PokeCoins</div>}
          {items.map(([key, quantity]) => (
            <div key={key} className="tiny">
              {quantity}× {key}
            </div>
          ))}
          {pokemon.length > 0 && (
            <>
              <div className="tiny">
                <strong>{pokemon.length} Pokémon</strong>
              </div>
              <div className="row row--wrap" style={{ gap: 4 }}>
                {pokemon.map((mon) => (
                  <span key={mon.id} className="give-confirm__mon" title={`${mon.displayName} · Lv ${mon.level}`}>
                    <img className="pixel" src={mon.sprite} alt="" />
                    <span className="tiny mono">Lv {mon.level}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {needsAck && (
          <div className="pxwell give-confirm__warn stack-sm" style={{ padding: 'var(--gap-sm)' }}>
            <strong className="tiny">⚠️ Some of these are hard to replace</strong>
            {notable.map((mon) => (
              <div key={mon.id} className="tiny">
                {mon.displayName} — {mon.notable!.map((reason) => reason.label).join(', ')}
              </div>
            ))}
            <label className="row tiny" style={{ gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              I understand these will be gone for good.
            </label>
          </div>
        )}

        <p className="tiny muted" style={{ margin: 0 }}>
          This cannot be undone. What you give stops existing.
        </p>

        <div className="row">
          <Button variant="ghost" onClick={onCancel} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button
            variant="go"
            loading={busy}
            disabled={busy || (needsAck && !acknowledged)}
            onClick={onConfirm}
            style={{ flex: 1 }}
          >
            {pokemon.length > 0 ? `Give ${pokemon.length} Pokémon` : 'Give it'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
